import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, MapPin, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ProductCard from '../components/ProductCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeaturedProducts();
  }, []);

  const fetchFeaturedProducts = async () => {
    try {
      const response = await axios.get(`${API}/products/featured`);
      setFeaturedProducts(response.data);
    } catch (error) {
      setFeaturedProducts([]);
      toast.error('No se pudieron cargar los productos destacados');
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    {
      name: 'Perfumes',
      image: 'https://images.unsplash.com/photo-1541643600914-78b084683601?w=800&q=80',
      description: 'Fragancias exclusivas'
    },
    {
      name: 'Labios',
      image: 'https://images.unsplash.com/photo-1586495777744-4413f21062fa?w=800&q=80',
      description: 'Color intenso'
    },
    {
      name: 'Rubores',
      image: 'https://images.pexels.com/photos/2417855/pexels-photo-2417855.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      description: 'Glow natural'
    },
    {
      name: 'Bases',
      image: 'https://images.pexels.com/photos/7256102/pexels-photo-7256102.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      description: 'Cobertura perfecta'
    },
    {
      name: 'Bolsas',
      image: 'https://images.unsplash.com/photo-1584917865442-de89df76afd3?w=800&q=80',
      description: 'Accesorios'
    }
  ];

  return (
    <div className="pt-20" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="/hero-image.jpeg"
            alt="Aliglam Beauty"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1C1917]/80 via-[#1C1917]/50 to-transparent"></div>
        </div>
        
        <div className="container-luxury relative z-10 py-20">
          <div className="max-w-2xl">
            <p className="text-sm tracking-[0.3em] uppercase text-[#D4A5A5] mb-6 animate-fade-in-up">
              Beauty Outlet & Makeup Store
            </p>
            <h1 
              className="text-6xl md:text-8xl font-light tracking-tighter leading-none mb-8 text-white animate-fade-in-up animation-delay-100"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Aliglam
            </h1>
            <p className="text-lg md:text-xl text-white/80 leading-relaxed mb-12 animate-fade-in-up animation-delay-200 max-w-lg">
              Descubre nuestra colección de productos de belleza premium. 
              Perfumes, maquillaje y más, seleccionados para realzar tu belleza natural.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-300">
              <Link 
                to="/shop" 
                className="btn-primary inline-flex items-center justify-center space-x-2"
                data-testid="shop-now-btn"
              >
                <span>Explorar Tienda</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link 
                to="/shop?featured=true" 
                className="btn-secondary inline-flex items-center justify-center space-x-2 !border-white/50 !text-white hover:!bg-white/10"
                data-testid="featured-btn"
              >
                <Sparkles className="w-4 h-4" />
                <span>Destacados</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="categories-section">
        <div className="container-luxury">
          <div className="text-center mb-16">
            <p className="text-sm tracking-[0.2em] uppercase text-[#A8A29E] mb-4">
              Explora
            </p>
            <h2 
              className="text-4xl md:text-5xl font-light tracking-tight text-[#1C1917]"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Categorías
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {categories.map((category, index) => (
              <Link
                key={category.name}
                to={`/shop?category=${encodeURIComponent(category.name)}`}
                className="category-card group"
                data-testid={`category-${category.name}`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="aspect-[3/4] relative overflow-hidden rounded-2xl">
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <p className="text-xs tracking-[0.15em] uppercase text-white/80 mb-1">
                      {category.description}
                    </p>
                    <h3 
                      className="text-xl font-light text-white"
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      {category.name}
                    </h3>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-20 md:py-32 bg-[#FAFAF9]" data-testid="featured-section">
        <div className="container-luxury">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16">
            <div>
              <p className="text-sm tracking-[0.2em] uppercase text-[#A8A29E] mb-4">
                Lo más vendido
              </p>
              <h2 
                className="text-4xl md:text-5xl font-light tracking-tight text-[#1C1917]"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Productos Destacados
              </h2>
            </div>
            <Link 
              to="/shop" 
              className="mt-6 md:mt-0 text-sm tracking-wide text-[#BA7B7C] hover:text-[#A36566] transition-colors inline-flex items-center space-x-2"
              data-testid="view-all-link"
            >
              <span>Ver todos</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
              {[...Array(4)].map((_, i) => (
                <div key={`skeleton-home-${i}`} className="animate-pulse">
                  <div className="bg-[#F5F0EB] aspect-[3/4] rounded-2xl mb-4"></div>
                  <div className="h-3 bg-[#F5F0EB] rounded w-1/3 mb-2"></div>
                  <div className="h-5 bg-[#F5F0EB] rounded w-2/3 mb-2"></div>
                  <div className="h-4 bg-[#F5F0EB] rounded w-1/4"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Delivery Info Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="delivery-section">
        <div className="container-luxury">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-[#F5F0EB] to-white rounded-3xl p-8 md:p-12 shadow-soft">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="w-20 h-20 md:w-24 md:h-24 bg-[#BA7B7C]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-10 h-10 md:w-12 md:h-12 text-[#BA7B7C]" />
                </div>
                <div className="text-center md:text-left flex-1">
                  <h3 
                    className="text-2xl md:text-3xl font-light text-[#1C1917] mb-4"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    Entregas Personales
                  </h3>
                  <p className="text-[#57534E] leading-relaxed mb-4">
                    Entregas personales mediante contacto por WhatsApp en Plaza Jardín o en la UTN (Universidad Tecnológica de Nezahualcóyotl).
                  </p>
                  <div className="flex items-center justify-center md:justify-start space-x-2 text-sm text-[#BA7B7C]">
                    <MessageCircle className="w-4 h-4" />
                    <span>Te contactamos vía WhatsApp para coordinar tu entrega</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#F5F0EB]" data-testid="cta-section">
        <div className="container-luxury">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-sm tracking-[0.2em] uppercase text-[#A8A29E] mb-4">
              Newsletter
            </p>
            <h2 
              className="text-4xl md:text-5xl font-light tracking-tight text-[#1C1917] mb-6"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Únete a nuestra comunidad
            </h2>
            <p className="text-[#57534E] mb-10 max-w-lg mx-auto">
              Suscríbete para recibir las últimas novedades, ofertas exclusivas 
              y consejos de belleza directamente en tu correo.
            </p>
            <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Tu correo electrónico"
                className="flex-1 px-6 py-4 rounded-full border border-[#E7E5E4] bg-white focus:outline-none focus:border-[#BA7B7C] transition-colors"
                data-testid="newsletter-email"
              />
              <button 
                type="submit" 
                className="btn-primary"
                data-testid="newsletter-submit"
              >
                Suscribirse
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
